import { ComponentProps } from 'react';
import clamp from 'lodash/clamp';
import styled, { css } from 'styled-components';
import { up } from '../../../utils/styles/breakpoints';
import { Box } from '../../layout/Box';
import { Flex } from '../../layout/Flex';

const MIN_POST_RATIO = 9 / 16;

export const Root = styled(Box).attrs((props) => ({
    ...props,
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    as: 'article',
}))`
    -webkit-user-select: none;
    user-select: none;
    transform: translateZ(0px);
`;

export const Header = styled(Flex).attrs((props) => ({
    ...props,
    as: 'header',
    direction: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    position: 'absolute',
    top: 0,
    left: 0,
    p: 2,
    width: '100%',
    zIndex: 5,
    spacing: 1,
}))``;

export const Content = styled(Flex)
    .withConfig({ displayName: 'Content' })
    .attrs((props) => ({
        ...props,
        position: 'absolute',
        direction: 'column',
        justifyContent: 'flex-end',
        px: 1,
        pb: 1.5,
        pt: 7.5,
        bottom: 0,
        left: 0,
        width: '100%',
        zIndex: 2,
    }))``;

interface ShadowProps {
    isVideo: boolean;
    isMobile: boolean;
    isVisible: boolean;
    isExpanded: boolean;
    isProductExpanded: boolean;
}

export const Shadow = styled(Box)
    .withConfig({ displayName: 'Shadow' })
    .attrs((props) => ({
        ...props,
        position: 'absolute',
        width: '100%',
        height: '100%',
        background: 'rgba(0, 0, 0, 0.70)',
        transition: '200ms linear opacity',
        cursor: 'pointer',
    }))<ShadowProps>`
            z-index: ${({ isVisible }) => (isVisible ? 1 : 10)};
    
            opacity: ${({ isExpanded, isVisible, isMobile }) =>
                isExpanded || (!isVisible && isMobile) ? 1 : 0};
            
            pointer-events: ${({ isExpanded, isProductExpanded, isVideo }) => {
                if (isProductExpanded && (isExpanded || isVideo)) {
                    return 'auto';
                }

                return 'none';
            }}
        `;

interface MediaCoverProps extends ComponentProps<typeof Box> {
    ratio: number;
    isMobile?: boolean;
    maxHeightOffset?: number;
}

export const PostMediaCover = styled(Box).attrs<MediaCoverProps, MediaCoverProps>((props) => ({
    ...props,
    display: 'block',
    overflow: 'hidden',
}))`
    position: relative;
    isolation: isolate;

    ${({ isMobile, ratio, maxHeightOffset = 0 }) => {
        if (!ratio) return;

        const clampedRatio = clamp(ratio, MIN_POST_RATIO, 1);
        let paddingBottom = `${(100 / clampedRatio).toFixed(2)}%`;

        if (isMobile) {
            paddingBottom =
                clampedRatio === MIN_POST_RATIO
                    ? `calc(100dvh - ${maxHeightOffset}px)`
                    : paddingBottom;
        }

        return css`
            &::after {
                content: '';
                display: block;
                width: 100%;
                height: 0;
                padding-bottom: ${paddingBottom};
            }
        `;
    }}

    ${up('sm')} {
        max-height: 100vh;
    }

    &&& > * {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 100%;
        height: 100%;
    }
`;

PostMediaCover.defaultProps = {
    backgroundColor: 'var(--placeholder)',
};
